import React from "react";
import { Check, CreditCard, MapPin, ShoppingBag, SlidersHorizontal, Star } from "lucide-react";

export function EatOrdering() {
  return (
    <section className="bg-white pb-20 pt-20 lg:pb-[124px]">
      <div className="mx-auto max-w-[1440px] px-[16px] sm:px-8">
        <p className="text-center text-[14px] font-semibold uppercase leading-5 tracking-[1.4px] text-[#017bef]">
          Ordering
        </p>
        <h2 className="pt-[12px] text-center text-[36px] font-bold leading-[1.2] tracking-[-1.2px] text-[#0a0a0a] sm:text-[48px] sm:leading-[48px]">
          Your Meal. Your Way.
        </h2>

        <div className="grid grid-cols-1 gap-6 pt-14 lg:grid-cols-3">
          <div className="relative flex min-h-[400px] items-end overflow-hidden rounded-3xl">
            <img
              src="/figma-assets/eat/delivery.jpg"
              alt="Delivery rider"
              className="absolute inset-0 size-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[rgba(10,10,10,0.85)] via-[rgba(10,10,10,0.2)] to-transparent" />
            <div className="relative p-7">
              <span className="inline-block rounded-full bg-[#017bef] px-[12px] py-1 text-[10px] font-bold uppercase leading-[15px] tracking-[1px] text-white">
                Delivery
              </span>
              <h3 className="pt-[16px] text-[20px] font-bold leading-7 text-white">Delivery</h3>
              <p className="pt-2 text-[14px] leading-[22.75px] text-white/60">
                Order your favourite food and have it sent to your destination.
              </p>
            </div>
          </div>

          <div className="flex min-h-[400px] flex-col justify-between rounded-3xl border-[1px] border-solid border-[#e5e7eb] bg-[#f8fbff] p-7">
            <div>
              <span className="inline-block rounded-full bg-[#017bef]/10 px-[12px] py-1 text-[10px] font-bold uppercase leading-[15px] tracking-[1px] text-[#017bef]">
                Pickup
              </span>
              <h3 className="pt-[20px] text-[20px] font-bold leading-7 text-[#0a0a0a]">Pickup</h3>
              <p className="pb-6 pt-2 text-[14px] leading-[22.75px] text-[#717182]">
                Order ahead and have your meal packed and ready for collection.
              </p>
            </div>
            <div className="rounded-2xl border-[1px] border-solid border-[#e5e7eb] bg-white p-[16px]">
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-semibold leading-4 text-[#0a0a0a]">Your order</span>
                <span className="rounded-full bg-[#dcfce7] px-2 py-0.5 text-[10px] font-bold leading-[15px] text-[#16a34a]">
                  Ready for pickup
                </span>
              </div>
              {["Item one", "Item two"].map(item => (
                <div key={item} className="flex items-center justify-between pt-[12px]">
                  <span className="text-[12px] leading-4 text-[#717182]">{item}</span>
                  <span className="size-2 rounded-full bg-[#017bef]/30" />
                </div>
              ))}
              <div className="mt-[12px] flex items-center gap-2 border-t-[1px] border-solid border-[#f5f8ff] pt-1">
                <MapPin className="size-[11px] text-[#017bef]" />
                <span className="text-[12px] leading-4 text-[#717182]">Collect at restaurant</span>
              </div>
            </div>
          </div>

          <div className="relative flex min-h-[400px] items-end overflow-hidden rounded-3xl">
            <img
              src="/figma-assets/eat/restaurant.jpg"
              alt="Restaurant dining room"
              className="absolute inset-0 size-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[rgba(1,45,110,0.85)] via-[rgba(1,45,110,0.2)] to-transparent" />
            <div className="relative p-7">
              <span className="inline-block rounded-full border-[1px] border-solid border-white/25 px-[12px] py-1 text-[10px] font-bold uppercase leading-[15px] tracking-[1px] text-white">
                Restaurant Ordering
              </span>
              <h3 className="pt-[16px] text-[20px] font-bold leading-7 text-white">Restaurant Ordering</h3>
              <p className="pt-2 text-[14px] leading-[22.75px] text-white/60">
                Order from restaurants on the SPACIA platform.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const STEPS = [
  { icon: Star, title: "Choose" },
  { icon: SlidersHorizontal, title: "Customise" },
  { icon: ShoppingBag, title: "Cart" },
  { icon: CreditCard, title: "Pay" },
  { icon: MapPin, title: "Track" },
  { icon: Check, title: "Enjoy" },
];

export function EatHowItWorks() {
  return (
    <section className="bg-[#f5f8ff] py-20">
      <div className="mx-auto flex max-w-[1440px] flex-col items-center px-[16px] sm:px-8">
        <p className="text-[14px] font-semibold uppercase leading-5 tracking-[1.4px] text-[#017bef]">How It Works</p>
        <h2 className="pt-[12px] text-center text-[30px] font-bold leading-10 tracking-[-0.9px] text-[#0a0a0a] sm:text-[36px]">
          From Browse to Delivered
        </h2>

        <div className="grid w-full max-w-[1024px] grid-cols-3 gap-[16px] pt-14 lg:grid-cols-6">
          {STEPS.map(({ icon: Icon, title }, index) => (
            <div key={title} className="relative">
              {/* Dashed link to the next card (desktop only) */}
              {index < STEPS.length - 1 && (
                <div className="absolute left-full top-[24px] hidden w-[16px] border-t-[1.5px] border-dashed border-[#017bef]/20 lg:block" />
              )}
              <div className="flex flex-col items-center rounded-2xl border-[1px] border-solid border-[#e5e7eb] bg-white p-[16px]">
                <span className="flex size-10 items-center justify-center rounded-[14px] bg-[#017bef]/[0.08]">
                  <Icon className="size-4 text-[#017bef]" />
                </span>
                <span className="pt-[12px] text-[10px] font-bold leading-[15px] tracking-[1px] text-[#717182]">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span className="pt-1 text-[14px] font-bold leading-5 text-[#0a0a0a]">{title}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
